import { useCallback } from 'react';
import { toast } from 'react-toastify';

import useSearchApi from '@/components/search/hooks/useSearchApi';
import useClientState from '@/state/useClientState';
import { PlankToMwdDragPayload } from '@/utils/plankToMwdDrag';
import { toastErrorOptions, toastInfoOptions, toastSuccessOptions } from '@/utils/toastOptions';

import { getResolvablePlankDropItems, toPlankDropPlan, toPlankDropSearchRequests } from '../utils/plankDropResolver';
import waitForWerklijnenInMap from '../utils/waitForWerklijnenInMap';

import { openWerklijnInMwd } from './openWerklijnInMwd';

/** Loads the dropped payload's werklijnen through the search API, then applies the plan from
 * the pure `plankDropResolver`. This hook only performs effects.
 */
export default function useOpenWerklijnFromPlankDrop() {
  const { searchAdvanced } = useSearchApi();

  return useCallback(
    async (payload: PlankToMwdDragPayload) => {
      const items = getResolvablePlankDropItems(payload);
      if (items.length === 0) {
        toast.info('Geen werklijn of eenheid gevonden voor het gesleepte item', toastInfoOptions);
        return;
      }

      let werklijnen;
      try {
        werklijnen = await searchAdvanced(toPlankDropSearchRequests(items), {
          add: true,
          suppressToast: true,
          showErrorToast: true,
        });
      } catch {
        toast.error('Zoeken naar de gesleepte werklijn is niet gelukt', toastErrorOptions);
        return;
      }

      const plan = toPlankDropPlan(items, werklijnen);
      if (plan.kind === 'none') {
        toast.info('Geen nieuwe werklijnen toegevoegd', toastInfoOptions);
        return;
      }

      const werklijnIds = plan.kind === 'batch' ? plan.werklijnIds : [plan.werklijnId];
      try {
        await waitForWerklijnenInMap(werklijnIds);
      } catch {
        toast.error('De gesleepte werklijn kon niet in MWD worden geladen', toastErrorOptions);
        return;
      }

      try {
        if (plan.kind === 'batch') {
          await useClientState.getState().vervangGeselecteerdeWerklijnen(plan.werklijnIds);
          toast.success(`Er zijn ${plan.werklijnIds.length} nieuwe werklijnen toegevoegd`, toastSuccessOptions);
          return;
        }
        await openWerklijnInMwd(plan.werklijnId, plan.werklijnElementId);
      } catch {
        toast.error('Toevoegen van werklijn in MWD is niet gelukt', toastErrorOptions);
      }
    },
    [searchAdvanced],
  );
}
